import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { useNavigate as useNav } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, Eye, Download, Search, Maximize } from 'lucide-react';

export default function Academics() {
  const navigate = useNav();
  const [academics, setAcademics] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE_URL}/GetAcademicDropDown`)
      .then(res => setAcademics(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error(err);
        setAcademics([]);
      });
  }, []);

  const getDocDetails = (name) => {
    const lower = (name || '').toLowerCase();
    let desc = "Official educational resource and circular guidelines.";
    let category = "General";

    if (lower.includes('syllabus') || lower.includes('curriculum')) {
      desc = "Curriculum coverage, chapters, and subject outlines.";
      category = "Syllabus";
    } else if (lower.includes('holiday') || lower.includes('calendar')) {
      desc = "Yearly event calendar, national holidays, and session plan.";
      category = "Calendars";
    } else if (lower.includes('book') || lower.includes('textbook')) {
      desc = "Prescribed school books, notebooks, and reference materials.";
      category = "Books";
    } else if (lower.includes('timetable') || lower.includes('schedule')) {
      desc = "Class timetables, assembly hours, and play schedules.";
      category = "Schedules";
    }

    return { desc, category };
  };

  const handleOpenPdf = (link) => {
    navigate(`/Documentation?file=${encodeURIComponent(link)}`);
  };

  const safeDocs = Array.isArray(academics) ? academics : [];
  const processedDocs = safeDocs.map(doc => {
    const details = getDocDetails(doc.Name);
    return {
      id: doc._id,
      name: doc.Name,
      link: doc.Link,
      desc: details.desc,
      category: details.category
    };
  });

  const filteredDocs = processedDocs.filter(doc => {
    const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.desc.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <div style={{ minHeight: '90vh', background: 'var(--bg-academics)', paddingTop: '120px', paddingBottom: '60px', fontFamily: 'Quicksand', position: 'relative' }}>

      {/* Floating clouds in sky blue background */}
      <div style={{ position: 'absolute', top: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', top: '8%', right: '8%', fontSize: '3rem', opacity: 0.15 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', top: '15%', left: '10%', fontSize: '2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '20%', right: '15%', fontSize: '2.5rem', opacity: 0.12 }} className="cloud-float">🎈</div>
      <div style={{ position: 'absolute', top: '30%', left: '4%', fontSize: '2rem', opacity: 0.2 }} className="plane-float">✈️</div>
      <div style={{ position: 'absolute', top: '40%', right: '6%', fontSize: '2.5rem', opacity: 0.12 }} className="float-slow">🎨</div>
      <div style={{ position: 'absolute', top: '50%', left: '8%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">✨</div>
      <div style={{ position: 'absolute', top: '58%', right: '12%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">✏️</div>
      <div style={{ position: 'absolute', top: '65%', left: '2%', fontSize: '2.8rem', opacity: 0.15 }} className="plane-float">🪁</div>
      <div style={{ position: 'absolute', top: '72%', right: '4%', fontSize: '2rem', opacity: 0.12 }}>📚</div>
      <div style={{ position: 'absolute', top: '80%', left: '6%', fontSize: '2.5rem', opacity: 0.15 }} className="cloud-float">🌈</div>
      <div style={{ position: 'absolute', top: '88%', right: '10%', fontSize: '2.2rem', opacity: 0.15 }} className="float-slow">🧸</div>
      <div style={{ position: 'absolute', bottom: '5%', left: '3%', fontSize: '2.5rem', opacity: 0.18 }} className="sun-spin">☀️</div>
      <div style={{ position: 'absolute', bottom: '8%', right: '7%', fontSize: '2rem', opacity: 0.15 }} className="cloud-float">☁️</div>
      <div style={{ position: 'absolute', bottom: '12%', left: '12%', fontSize: '2.3rem', opacity: 0.15 }} className="float-slow">🍎</div>
      <div style={{ position: 'absolute', bottom: '2%', right: '3%', fontSize: '2rem', opacity: 0.15 }}>🖍️</div>

      <div className="page-container-wide">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: '50px' }}
        >
          <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--primary-dark)', textTransform: 'uppercase', letterSpacing: '1.5px' }}>Academic Portal</span>
          <h1 style={{ fontSize: '2.75rem', color: 'var(--text-primary)', marginBottom: '12px', fontFamily: 'Poppins', fontWeight: 800 }}>Curriculum & Resources</h1>
          <p style={{ color: 'var(--text-secondary)', fontWeight: 700, maxWidth: '600px', margin: '0 auto', fontSize: '1.05rem' }}>Access all digital syllabus outlines, class schedules, and prescribed study documents.</p>
        </motion.div>

        {/* Filter Toolbar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={filterToolbarStyle}
        >
          <div style={searchBoxStyle}>
            <Search size={18} style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search syllabus, books, calendars..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={searchInputStyle}
            />
          </div>

        </motion.div>

        {/* Documents Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '30px', marginTop: '40px' }}>
          <AnimatePresence>
            {filteredDocs.map((doc, idx) => (
              <motion.div
                key={doc.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.4, delay: idx * 0.08 }}
                whileHover={{ boxShadow: 'var(--shadow-lg)' }}
                style={docCardStyle(doc.category)}
              >
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' }}>
                  <div style={iconBadgeStyle(doc.category)}>
                    <FileText size={24} />
                  </div>
                  <div>
                    <span style={categoryBadgeStyle(doc.category)}>{doc.category}</span>
                    <h3 style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: '6px 0 8px', fontWeight: 800, fontFamily: 'Poppins', lineHeight: '1.3' }}>{doc.name}</h3>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: '1.5', margin: 0, fontWeight: 600 }}>{doc.desc}</p>
                  </div>
                </div>

                <div style={{ marginTop: 'auto', display: 'flex', gap: '10px', pt: '16px', borderTop: '1px solid var(--border-color)' }}>
                  <button
                    onClick={() => handleOpenPdf(doc.link)}
                    className="btn-premium btn-premium-primary"
                    style={actionBtnStyle}
                  >
                    <Eye size={14} /> Preview
                  </button>
                  <a
                    href={doc.link.replace('/preview', '/export?format=pdf')}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="btn-premium btn-premium-secondary"
                    style={{ ...actionBtnStyle, flexGrow: 0, width: '40px', padding: 0 }}
                    title="Download PDF"
                  >
                    <Download size={14} />
                  </a>
                  <button
                    onClick={() => window.open(doc.link, '_blank')}
                    className="btn-premium btn-premium-outline"
                    style={{ ...actionBtnStyle, flexGrow: 0, width: '40px', padding: 0 }}
                    title="View Fullscreen"
                  >
                    <Maximize size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredDocs.length === 0 && (
          <div style={{ textAlign: 'center', padding: '64px 0', backgroundColor: '#ffffff', borderRadius: 'var(--border-radius-card)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-md)' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', fontWeight: 600 }}>No documents match your query.</p>
          </div>
        )}

      </div>
    </div>
  );
}

// Styling parameters
const filterToolbarStyle = {
  backgroundColor: '#ffffff',
  padding: '24px',
  borderRadius: 'var(--border-radius-card)',
  boxShadow: 'var(--shadow-md)',
  border: '1px solid var(--border-color)',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const searchBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius-form)',
  padding: '0 16px',
  backgroundColor: 'var(--bg-color)'
};

const searchInputStyle = {
  border: 'none',
  background: 'transparent',
  padding: '14px 0',
  width: '100%',
  outline: 'none',
  fontSize: '0.98rem',
  color: 'var(--text-primary)',
  fontWeight: 600,
  fontFamily: 'Quicksand'
};



const docCardStyle = (category) => {
  let borderCol = 'var(--primary-color)';
  if (category === 'Syllabus') borderCol = 'var(--accent-coral)';
  if (category === 'Calendars') borderCol = 'var(--secondary-color)';
  if (category === 'Books') borderCol = 'var(--accent-green)';
  if (category === 'Schedules') borderCol = 'var(--accent-purple)';

  return {
    backgroundColor: '#ffffff',
    padding: '30px',
    borderRadius: 'var(--border-radius-card)',
    border: '1px solid var(--border-color)',
    borderTop: `8px solid ${borderCol}`,
    boxShadow: 'var(--shadow-md)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: '260px'
  };
};

const iconBadgeStyle = (category) => {
  let bg = 'rgba(96, 165, 250, 0.1)';
  let color = 'var(--primary-dark)';

  if (category === 'Syllabus') { bg = 'rgba(251, 113, 133, 0.1)'; color = '#f43f5e'; }
  if (category === 'Calendars') { bg = 'rgba(250, 204, 21, 0.15)'; color = '#d97706'; }
  if (category === 'Books') { bg = 'rgba(74, 222, 128, 0.1)'; color = '#16a34a'; }
  if (category === 'Schedules') { bg = 'rgba(167, 139, 250, 0.1)'; color = 'var(--accent-purple)'; }

  return {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    backgroundColor: bg,
    color: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  };
};

const categoryBadgeStyle = (category) => {
  let bg = 'rgba(96, 165, 250, 0.1)';
  let color = 'var(--primary-dark)';

  if (category === 'Syllabus') { bg = 'rgba(251, 113, 133, 0.1)'; color = '#f43f5e'; }
  if (category === 'Calendars') { bg = 'rgba(250, 204, 21, 0.15)'; color = '#d97706'; }
  if (category === 'Books') { bg = 'rgba(74, 222, 128, 0.1)'; color = '#16a34a'; }
  if (category === 'Schedules') { bg = 'rgba(167, 139, 250, 0.1)'; color = 'var(--accent-purple)'; }

  return {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '4px',
    backgroundColor: bg,
    color: color,
    fontSize: '0.72rem',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };
};

const actionBtnStyle = {
  height: '36px',
  padding: '0 12px',
  fontSize: '0.82rem',
  borderRadius: '10px',
  flexGrow: 1
};
